<script setup lang="ts">
import type {
  ExternalCourse,
  PublicFormVerificationStatus,
  SubjectRequest,
  WorkExperience,
} from '@/models/current-student-advanced-standing-form/api'
import PreviousLearningExternalCoursesTable from '@/components/current-student-advanced-standing-form/previous-learning/PreviousLearningExternalCoursesTable.vue'
import PreviousLearningWorkExperienceTable from '@/components/current-student-advanced-standing-form/previous-learning/PreviousLearningWorkExperienceTable.vue'
import type { LegacyClassBinding } from '@/components/current-student-advanced-standing-form/shared/types'

defineProps<{
  previousLearningExternalCourses: ExternalCourse[]
  workExperiences: WorkExperience[]
  externalCourseRowClass: (course: ExternalCourse) => LegacyClassBinding
  externalCourseSubjectRowspan: (course: ExternalCourse) => number
  workExperienceRowClass: (workExperience: WorkExperience) => LegacyClassBinding
  workExperienceSubjectRowspan: (workExperience: WorkExperience) => number
  isVerifiedStatus: (status: PublicFormVerificationStatus) => boolean
  canEditExternalCourse: (course: ExternalCourse) => boolean
  canEditWorkExperience: (workExperience: WorkExperience) => boolean
  workExperienceMissingRequestedSubject: boolean
  missingWorkExperienceSubjectMessage: string
}>()

const emit = defineEmits<{
  addExternalCourse: []
  editExternalCourse: [course: ExternalCourse]
  addExternalCourseSubjectRequest: [course: ExternalCourse]
  deleteExternalCourseSubjectRequest: [course: ExternalCourse, request: SubjectRequest]
  addWorkExperience: []
  editWorkExperience: [workExperience: WorkExperience]
  addWorkExperienceSubjectRequest: [workExperience: WorkExperience]
  deleteWorkExperienceSubjectRequest: [workExperience: WorkExperience, request: SubjectRequest]
}>()
</script>

<template>
  <fieldset>
    <legend>Previous Tertiary Courses</legend>
    <p>What tertiary courses have you studied previously?</p>
    <p>Please do not enter industry-based certifications in this step, you will be able to enter those in Step 6.</p>
    NOTES:
    <ol>
      <li>Courses are referring to the overarching degree that you studied, for example a 'Bachelor of Arts' is a course.</li>
      <li>Industry-based certifications refer to any qualifications that are not part of the <a href="https://www.aqf.edu.au/" target="_blank" rel="noreferrer">Australian Qualifications Framework</a>.</li>
      <li>
        <b>DO NOT</b>
        attempt to add previous La Trobe courses to this list, they will appear here automatically.<br>
        If your La Trobe course does not appear here automatically please contact <a href="http://latrobe-current.custhelp.com/app/home" target="_blank" rel="noreferrer">ASK La Trobe.</a>
      </li>
    </ol>
    <PreviousLearningExternalCoursesTable
      :courses="previousLearningExternalCourses"
      :row-class="externalCourseRowClass"
      :rowspan="externalCourseSubjectRowspan"
      :is-verified-status="isVerifiedStatus"
      :can-edit-external-course="canEditExternalCourse"
      @edit-course="emit('editExternalCourse', $event)"
      @add-subject-request="emit('addExternalCourseSubjectRequest', $event)"
      @delete-subject-request="(course, request) => emit('deleteExternalCourseSubjectRequest', course, request)"
    />
    <button class="btn btn-primary" id="addExtCourseBtn" type="button" @click="emit('addExternalCourse')">Add Course</button>
  </fieldset>

  <fieldset>
    <legend>Work Experience &amp; Industry-Based Certifications</legend>
    <p>Have you completed any work experience or industry-based certifications that are relevant to your course?</p>
    <PreviousLearningWorkExperienceTable
      :work-experiences="workExperiences"
      :row-class="workExperienceRowClass"
      :rowspan="workExperienceSubjectRowspan"
      :is-verified-status="isVerifiedStatus"
      :can-edit-work-experience="canEditWorkExperience"
      @edit-work-experience="emit('editWorkExperience', $event)"
      @add-subject-request="emit('addWorkExperienceSubjectRequest', $event)"
      @delete-subject-request="(workExperience, request) => emit('deleteWorkExperienceSubjectRequest', workExperience, request)"
    />
    <button class="btn btn-primary" id="addWorkExperienceBtn" type="button" @click="emit('addWorkExperience')">Add Work Experience</button>
    <p></p>
    <div
      class="alert alert-danger"
      id="workExpMissingRequestedSubjectAlert"
      :class="{ hidden: !workExperienceMissingRequestedSubject }"
    >
      {{ missingWorkExperienceSubjectMessage }}
    </div>
  </fieldset>
</template>
